<div class="organisations">
  @if (isTableInitiating) {
    <app-action-buttons-skeleton></app-action-buttons-skeleton>
    <app-table-skeleton></app-table-skeleton>
  } @else {
    <div class="organisations__toolbar">
      <div class="organisations__scroll-group" (scroll)="onToolbarScroll()">
        <div class="organisations__search">
          <mat-icon class="organisations__search-icon">search</mat-icon>
          <input
            class="organisations__search-input"
            type="text"
            [formControl]="searchControl"
            placeholder="Filter by organisation name or customer number…" />
          @if (searchControl.value) {
            <button type="button" class="organisations__search-clear" aria-label="Clear search" (click)="clearSearch()">
              <mat-icon>close</mat-icon>
            </button>
          }
        </div>
        <span class="organisations__count">{{ totalCount }} organisation{{ totalCount === 1 ? '' : 's' }}</span>
        <div class="organisations__add">
          <!-- Gated on isLoading (the assigned-organisations fetch), not the unassigned-parties
          prefetch the popover itself depends on — that one runs in the background and finishes
          well after this button is usable; the popover shows its own loading/error state. -->
          <button
            #addOrgButton
            type="button"
            class="m-btn m-btn-primary"
            [disabled]="isLoading"
            (click)="toggleAddOrg()">
            <mat-icon>add</mat-icon>
            Add organisation
            <span class="sgl-arrows" aria-hidden="true"></span>
          </button>
          @if (addOrgOpen) {
            <app-add-organisation-popover
              [anchorTop]="addOrgAnchor.top"
              [anchorRight]="addOrgAnchor.right"
              (picked)="onPartyPicked($event)"
              (closed)="addOrgOpen = false"></app-add-organisation-popover>
          }
        </div>
      </div>
    </div>

    <div class="organisations__table-wrapper">
      <!--
      The header lives outside cdk-virtual-scroll-viewport for the same reason as the Contacts
      grid: the viewport fakes scroll position with a `transform: translateY(...)` on its content
      wrapper, and that transform creates a new containing block for any `position: sticky`
      descendant, so a sticky header rendered inside the viewport drifts instead of staying
      pinned. See ../../../contacts/VIRTUAL_SCROLL.md "Gotcha #2" for the full writeup.

      As with Contacts, this means the table body has no header row/matHeaderRowDef — mat-sort
      still works on the header <div>s since it's an attribute directive that only needs a MatSort
      ancestor. #headerRow's scrollLeft is synced to the viewport's on every scroll
      (onViewportScroll()) so the header still tracks horizontal scroll below the table's
      min-width floor.
    -->
      <div #headerRow class="organisations__header-row">
        <div class="organisations__header-row-inner" matSort (matSortChange)="onSort($event)" role="row">
          <div
            class="organisations__header-cell organisations__header-cell--organisation"
            mat-sort-header="organisation"
            role="columnheader">
            Organisation
          </div>
          <div
            class="organisations__header-cell organisations__header-cell--partyNumber"
            mat-sort-header="partyNumber"
            role="columnheader">
            Customer number
          </div>
          <div
            class="organisations__header-cell organisations__header-cell--activeUsers"
            mat-sort-header="activeUsers"
            role="columnheader">
            Active users
          </div>
          <div
            class="organisations__header-cell organisations__header-cell--activeDepartments"
            mat-sort-header="activeDepartments"
            role="columnheader">
            Active departments
          </div>
          <div
            class="organisations__header-cell organisations__header-cell--inactiveCount"
            mat-sort-header="inactiveCount"
            role="columnheader">
            Inactive
          </div>
        </div>
      </div>
      <cdk-virtual-scroll-viewport
        [itemSize]="rowHeight"
        class="organisations__viewport"
        (scroll)="onViewportScroll($event)">
        <table
          mat-table
          recycleRows
          [dataSource]="dataSource"
          class="organisations__table"
          [attr.aria-rowcount]="totalCount">
          <colgroup>
            @for (column of displayedColumns; track column) {
              <col class="organisations__col--{{ column }}" />
            }
          </colgroup>
          <ng-container matColumnDef="organisation">
            <td mat-cell *matCellDef="let row">
              <div class="organisations__cell-organisation">
                <span class="organisations__avatar"><mat-icon>business</mat-icon></span>
                <button type="button" class="organisations__link" (click)="openRow(row)">{{ row.partyName }}</button>
              </div>
            </td>
          </ng-container>
          <ng-container matColumnDef="partyNumber">
            <td mat-cell *matCellDef="let row">{{ row.partyNumber }}</td>
          </ng-container>
          <ng-container matColumnDef="activeUsers">
            <td mat-cell *matCellDef="let row">{{ row.activeUsersCount }}</td>
          </ng-container>
          <ng-container matColumnDef="activeDepartments">
            <td mat-cell *matCellDef="let row">{{ row.activeDepartmentsCount }}</td>
          </ng-container>
          <ng-container matColumnDef="inactiveCount">
            <td mat-cell *matCellDef="let row" [class.organisations__inactive--flagged]="row.inactiveCount > 0">
              {{ inactiveDisplay(row.inactiveCount) }}
            </td>
          </ng-container>
          <tr
            mat-row
            *matRowDef="let row; columns: displayedColumns"
            class="organisations__row"
            [attr.aria-rowindex]="row.rowIndex + 2"></tr>
        </table>
      </cdk-virtual-scroll-viewport>

      @if (!isLoading && totalCount === 0) {
        <div class="organisations__empty">
          {{ hasError ? 'Failed to load organisations. Please try again.' : 'No organisations match your filter.' }}
        </div>
      }
      @if (isLoading) {
        <div class="organisations__spinner">
          <mat-spinner diameter="60"></mat-spinner>
        </div>
      }
    </div>
  }
</div>
